import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Metro } from '../../types/metro';
import { getSectorData, ratioColor } from '../../lib/map-utils';
import { RankingsSeverityPill } from '../rankings/RankingsSeverityPill';
import { formatNumber, shortageCell } from '../../lib/utils';
import { lensedRatioForMetro } from '../../lib/occupation-utils';
import { useFilterStore } from '../../stores/filterStore';

/**
 * Sort direction for the popup's per-group ratio sort.
 * Local to the popup component -- the Rankings page has its own
 * SortDirection type (`hooks/useRankings`) wired to multi-column
 * sorting; the popup only sorts the Ratio column, so a 2-state local
 * type avoids importing the cross-cutting hook + reduces coupling.
 */
type PopupSortDir = 'asc' | 'desc';

interface MetroPopupProps {
  readonly metro: Metro;
  /**
   * Close handler -- caller supplies this so the same popup body
   * works in two surfaces:
   *   - Desktop: rendered inside Leaflet's `<Popup>` chrome; close
   *     handler delegates to `useMap().closePopup()` (from MetroBubble).
   *   - Mobile: rendered as a full-screen modal at the layout level;
   *     close handler clears the selected metro in `useMetroStore`.
   *
   * Decoupling the close action from `useMap` also lets the component
   * be unit-tested without mocking react-leaflet.
   */
  readonly onClose: () => void;
  /**
   * Hint about the surface this popup is being rendered in. Drives
   * whether the per-group table caps its own height:
   *   - `popup` (default) -- Leaflet popup chrome on desktop. The
   *     table caps at 340px so the popup doesn't dominate the map.
   *   - `modal` -- mobile full-viewport modal. The table grows
   *     naturally and the modal's own card scroll-container handles
   *     overflow, so the user can use the full screen height to
   *     scan occupational groups (Maria 2026-06-05 ask).
   */
  readonly surface?: 'popup' | 'modal';
  /**
   * Optional expand handler. When supplied and `surface === 'popup'`,
   * an "expand" icon button appears next to the close button. Callers
   * use this to promote the compact Leaflet popup into a full-viewport
   * modal on desktop (2026-07-03 V2 feedback follow-up: reviewers
   * asked for a way to enlarge the popup after clicking a metro).
   */
  readonly onExpand?: () => void;
}

/**
 * Click-popup body for a metro bubble. 2026-05-18 feedback pass
 * replaced the prior 2×2 stats grid + ratio-chip sector list with a
 * proper 5-column table:
 *
 *   Springboard occupational group · Credentials · Job openings · Ratio · Shortage
 *
 * The top-of-popup credentials/jobs/shortage summary line was
 * removed in the same pass — the table now carries all of those
 * numbers, broken down per group, so the duplicate summary doesn't
 * add information.
 *
 * Wrapped by Leaflet's `<Popup>` chrome in MetroBubble — the
 * wrapper itself is restyled via `.leaflet-popup-content-wrapper`
 * overrides in `globals.css`. Leaflet's default close button is
 * disabled (`closeButton={false}`) since we render our own.
 */
// 2026-07-02 (Georgetown feedback): every numeric column in the
// popup table is sortable. Users often want to see the metro's
// biggest raw credentials shortage / largest job pool / highest
// ratio -- not just the ratio. Sort state is (key, dir) and the
// arrow indicator lives in whichever column is active.
type PopupSortKey = 'credentials' | 'jobs' | 'ratio' | 'shortage';

export function MetroPopup({ metro, onClose, surface = 'popup', onExpand }: MetroPopupProps) {
  // Default sort is ratio ASCENDING so the greatest shortage (lowest
  // ratio) lands at the top -- Zack's original IMP-001 spec.
  const [sortKey, setSortKey] = useState<PopupSortKey>('ratio');
  const [sortDir, setSortDir] = useState<PopupSortDir>('asc');
  // CR-06 (2026-07-08, GX5): the bubble underneath this popup already
  // lenses its color/size to the selected occupational-group filter;
  // the popup's headline had no ratio number at all (removed in the
  // 2026-05-18 pass), which left the OG-filtered click experience with
  // no headline confirmation of which number the user is looking at.
  // The per-group TABLE below already shows every group (including the
  // selected one) unfiltered -- this only adds an honest headline.
  const occupationalGroupId = useFilterStore((s) => s.occupationalGroupId);
  const lens = lensedRatioForMetro(metro, occupationalGroupId);
  const sectors = useMemo(() => {
    const base = getSectorData(metro);
    // Stable sort: clone so we don't mutate the source array (the
    // raw array is memoized by getSectorData() callers in other
    // tests, and Array.prototype.sort mutates in place).
    const sorted = [...base].sort((a, b) => {
      let av: number;
      let bv: number;
      switch (sortKey) {
        case 'credentials':
          // CR-013: sort on Georgetown's supplied credentials, matching the
          // rendered cell. Null (seed-math fallback) sorts as 0.
          av = a.credentials ?? 0;
          bv = b.credentials ?? 0;
          break;
        case 'jobs':
          av = a.jobs;
          bv = b.jobs;
          break;
        case 'shortage':
          // CR-013: sort on the supplied shortage column, not a
          // jobs-minus-credentials reconstruction. Null sorts as 0.
          av = a.shortage ?? 0;
          bv = b.shortage ?? 0;
          break;
        case 'ratio':
        default:
          av = a.ratio;
          bv = b.ratio;
      }
      return av - bv;
    });
    return sortDir === 'asc' ? sorted : sorted.reverse();
  }, [metro, sortKey, sortDir]);
  /**
   * Click a header. If it's the active column, flip direction;
   * otherwise switch to it and default to ascending.
   */
  const handleSort = (key: PopupSortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  const ariaSortFor = (key: PopupSortKey): 'ascending' | 'descending' | 'none' =>
    key === sortKey ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none';

  // Width is governed by the parent surface: Leaflet's `<Popup>` sets
  // `minWidth={560}` + `maxWidth={560}` on desktop, the mobile modal
  // wraps this in a `max-w-[560px] w-full` card. Either way, this
  // inner body should fill the surface, so we use `w-full` rather
  // than the previous fixed `w-[560px]` (which would overflow the
  // mobile modal on narrow phones).
  //
  // IMP-005 (mobile popup pass, 2026-06-05 spec):
  //   Section 1 - modal header (sticky, NOT scrollable)
  //   Section 2 - table column headers (sticky vertically, scrolls
  //               HORIZONTALLY in lockstep with the table body)
  //   Section 3 - table body (scrolls vertically AND horizontally)
  //   Section 4 - quick nav (sticky, NOT scrollable)
  //
  // We get section 1 + 4 (locked, never move) by placing them
  // outside the scroll container -- they're rows 1 and 3 of a
  // 3-row CSS Grid (auto / 1fr / auto) and the table sits in the
  // middle row's scroll container.
  //
  // We get section 2 + 3 sharing a single horizontal scroll
  // context naturally by keeping `<thead>` and `<tbody>` inside
  // the same `<table>`. CSS `position: sticky; top: 0` on the
  // thead pins it vertically within the scroll container while
  // letting it slide horizontally with the body when columns
  // exceed the viewport.
  //
  // The h-full + max-h-full pair is the bridge: the parent modal
  // card carries a DEFINITE height so `h-full` resolves to a
  // concrete value, which lets the grid's 1fr row receive a real
  // size constraint. On the desktop `popup` surface this whole
  // dance is skipped -- the table is just inside a max-h-[340px]
  // overflow-y-auto wrapper as before.
  const isModal = surface === 'modal';
  return (
    <div
      className={`relative w-full font-sans ${
        isModal
          ? 'grid h-full max-h-full grid-rows-[auto_minmax(0,1fr)_auto]'
          : ''
      }`}
    >
      {/* Head — metro name + close button. Status badge removed in
          2026-05-18 pass; the new visual encoding is continuous and
          the badge would be redundant with the colored ratio cell in
          the table below. On the mobile modal surface this is grid
          row 1 (auto height); no sticky positioning needed. */}
      <div
        className="border-b border-border bg-white px-[14px] pt-[14px] pb-[10px]"
      >
        {/* Header controls -- expand + close. On the compact popup
            surface we render both; the modal surface only shows the
            close X (nothing to expand into). */}
        <div className="absolute right-[10px] top-[10px] flex items-center gap-1.5">
          {onExpand && !isModal && (
            <button
              type="button"
              aria-label="Expand popup to full screen"
              title="Expand"
              onClick={onExpand}
              className="flex h-[22px] w-[22px] items-center justify-center rounded-full border-0 bg-border-light text-text-2 transition hover:bg-border"
            >
              {/* Arrows-out-of-corners icon */}
              <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" fill="none">
                <path
                  d="M1 4V1H4M9 4V1H6M1 6V9H4M9 6V9H6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
          <button
            type="button"
            aria-label="Close popup"
            onClick={onClose}
            className="flex h-[22px] w-[22px] items-center justify-center rounded-full border-0 bg-border-light text-text-2 transition hover:bg-border"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" fill="none">
              <path
                d="M1 1L9 9M9 1L1 9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <h3 className="pr-16 text-[14px] font-bold leading-[1.3] text-text-1">{metro.name}</h3>
        {/* Headline ratio -- lensed to the selected OG filter when
            active (CR-06). isLensed gates the "in {group}" qualifier
            so we never label the overall-ratio fallback as if it were
            OG-specific. */}
        <div className="mt-1 flex items-baseline gap-1.5">
          <span
            className="text-[18px] font-extrabold leading-none"
            style={{ color: ratioColor(lens.ratio) }}
          >
            {lens.ratio.toFixed(2)}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-[0.05em] text-text-4">
            {lens.isLensed
              ? `credentials-to-jobs ratio in ${lens.lensSectorName}`
              : 'overall credentials-to-jobs ratio'}
          </span>
        </div>
        <p className="mt-1 text-[11px] text-text-4">
          Per-group credentials and job openings. Ratio below 1.0 = shortage.
        </p>
      </div>

      {/* Table — 5 columns. Header row uses uppercase eyebrow text;
          body rows show one occupational group per row. The "Credentials"
          and "Shortage" columns read Georgetown's supplied values
          (`awardsSb` / `shortage`) straight through — CR-013; they used to
          be reconstructed from jobs×ratio, which misstated the numbers
          Emma sent. Surplus (and rounded-to-zero) rows print "—" so the
          eye doesn't accidentally read a positive number as a shortage.

          IMP-005 (density pass, 2026-06): trimmed row padding from
          `py-2.5` to `py-1.5`, dropped body type from 14px to 12px,
          and bumped the scroll container to `max-h-[340px]` so more
          of the 22 occupational groups are visible at once without
          scrolling. Header height dropped from `h-9` to `h-8` to
          match the tighter rhythm. The dot indicator shrinks 8→6px
          so it stays vertically centered with the smaller text.

          IMP-005 (mobile popup pass, 2026-06-05 final-final): on
          the mobile modal surface this is the grid's 1fr middle
          row. It owns BOTH axes of scroll: vertical for the 22
          occupational groups, horizontal in case the columns
          don't fit a narrow phone. The header above (grid row 1)
          and quick-nav below (grid row 3) are siblings so they
          never move with the user's scrolling -- only this region
          scrolls. On the desktop popup surface this is the
          single scroll area, capped at 340px. */}
      <div
        className={
          isModal
            ? 'metro-popup-scroll min-h-0 overflow-auto'
            : 'max-h-[340px] overflow-y-auto'
        }
      >
        <table className="w-full border-collapse text-left">
          {/* Sticky thead pins to the top of its own scroll container
              -- on mobile that's the grid 1fr row (right under the
              fixed modal header), on desktop it's the max-h-[340px]
              scroll div. `top-0` works for both because the sticky
              context is the immediate scroll ancestor. */}
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="border-b-2 border-navy">
              <th className="px-3 py-2 align-bottom text-left text-[12px] font-semibold text-text-3">
                Springboard occupational group
              </th>
              {/* 2026-07-02 (Georgetown feedback): every numeric column
                  is now clickable-sortable (was ratio-only). Same
                  visual grammar as the Ratio column that shipped
                  under IMP-001 -- always-visible caret + aria-sort. */}
              <SortableHeader
                label="Annual credentials awarded"
                sortKey="credentials"
                activeKey={sortKey}
                dir={sortDir}
                onSort={handleSort}
                align="right"
                ariaSort={ariaSortFor('credentials')}
              />
              <SortableHeader
                label="Annual job openings"
                sortKey="jobs"
                activeKey={sortKey}
                dir={sortDir}
                onSort={handleSort}
                align="right"
                ariaSort={ariaSortFor('jobs')}
              />
              <SortableHeader
                label="Credentials-to-jobs ratio"
                sortKey="ratio"
                activeKey={sortKey}
                dir={sortDir}
                onSort={handleSort}
                align="center"
                ariaSort={ariaSortFor('ratio')}
              />
              <SortableHeader
                label="Annual credential shortage"
                sortKey="shortage"
                activeKey={sortKey}
                dir={sortDir}
                onSort={handleSort}
                align="right"
                ariaSort={ariaSortFor('shortage')}
              />
            </tr>
          </thead>
          <tbody>
            {sectors.map((sec) => {
              // CR-013: read Georgetown's supplied shortage column, not a
              // jobs-minus-credentials reconstruction. null in the seed-math
              // fallback path. CR-017: shortageCell() reserves the em-dash for
              // surplus (status), showing 0 for a rounded-down shortage.
              const shortage = sec.shortage;
              return (
                <tr key={sec.name} className="border-b border-border-light text-[12px] text-text-2 transition-colors hover:bg-ice">
                  <td className="px-3 py-1.5 text-left">
                    <span className="flex items-start gap-2 leading-[1.3]">
                      <span
                        aria-hidden="true"
                        className="mt-[4px] h-[6px] w-[6px] shrink-0 rounded-full"
                        style={{ background: sec.color }}
                      />
                      <span title={sec.name}>{sec.name}</span>
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                    {sec.credentials != null ? formatNumber(sec.credentials) : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                    {formatNumber(sec.jobs)}
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    <RankingsSeverityPill ratio={sec.ratio} />
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                    {shortageCell(sec.status, shortage)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Quick navigation -- row-peek-drawer pattern translated to
          the Leaflet popup geometry. Lets the user jump from the
          map popup to Detail / Providers / Compare for this metro
          without first closing the popup, hunting for the metro in
          another tab, etc.

          IMP-005 (density pass, 2026-06): single-word labels (Detail
          / Providers / Compare) so the section claims less vertical
          space and the table above gets more room. Long-form labels
          survive on the `title` attribute for hover-tooltip / a11y
          discoverability.

          IMP-005 (mobile popup pass, 2026-06-05 final): on the
          mobile modal surface this block is `flex-none` so it
          stays pinned at the bottom of the modal -- the user
          always sees Detail / Providers / Compare regardless of
          how far they've scrolled into the table above. Only the
          table region between the pinned header + pinned quick
          nav scrolls.

          IMP-006 (horizontal quick-nav pass, 2026-06): switched from
          a 3-row vertical stack (with eyebrow label) to a horizontal
          inline-button row at >=360px viewports. Drops the
          "Quick navigation" eyebrow (the row of 3 buttons is
          self-evidently nav), trims footer padding from `py-2` to
          `py-1.5`, and yields ~80-100px of vertical reclaim for the
          occupational-groups table above -- roughly 6-8 more OG
          rows visible without scrolling. Below 360px the layout
          stacks vertically (`flex-col`) since 3 buttons in a row on
          a phone-sized popup get cramped. */}
      <div className="border-t border-border-light bg-white px-[14px] py-1.5">
        <div className="flex flex-col gap-1.5 min-[360px]:flex-row min-[360px]:gap-2">
          <Link
            to={`/detail?metroId=${metro.id}`}
            title="View detail page"
            className="inline-flex items-center justify-center gap-1 rounded-lg border border-navy/15 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-navy transition-colors hover:bg-navy/5 focus:outline-none focus:ring-2 focus:ring-blue/40 min-[360px]:flex-1"
          >
            <span>Detail</span>
            <span aria-hidden="true">&rarr;</span>
          </Link>
          <Link
            to={`/compare?metroIds=${metro.id}`}
            title="Add to comparison"
            className="inline-flex items-center justify-center gap-1 rounded-lg border border-navy/15 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-navy transition-colors hover:bg-navy/5 focus:outline-none focus:ring-2 focus:ring-blue/40 min-[360px]:flex-1"
          >
            <span>Compare</span>
            <span aria-hidden="true">&rarr;</span>
          </Link>
          <Link
            to={`/providers?metroId=${metro.id}`}
            title="Find providers in this metro"
            className="inline-flex items-center justify-center gap-1 rounded-lg border border-navy/15 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-navy transition-colors hover:bg-navy/5 focus:outline-none focus:ring-2 focus:ring-blue/40 min-[360px]:flex-1"
          >
            <span>Providers</span>
            <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// SortableHeader — reusable <th> that carries the popup's sort
// affordance for one numeric column. Renders the label + a caret,
// and calls `onSort(sortKey)` when clicked. `aria-sort` is set to
// "none" when this column isn't the active sort so screen readers
// can distinguish the active column from the dormant ones.
// -------------------------------------------------------------------
interface SortableHeaderProps {
  readonly label: string;
  readonly sortKey: PopupSortKey;
  readonly activeKey: PopupSortKey;
  readonly dir: PopupSortDir;
  readonly onSort: (key: PopupSortKey) => void;
  readonly align: 'left' | 'right' | 'center';
  readonly ariaSort: 'ascending' | 'descending' | 'none';
}

function SortableHeader({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
  align,
  ariaSort,
}: SortableHeaderProps) {
  const isActive = sortKey === activeKey;
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  const buttonJustify =
    align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      // CR-003: labels grew 2-4x ("Credentials" -> "Annual credentials
      // awarded"), so headers now wrap to 2-3 lines within a fixed-ish
      // numeric column width rather than widening the 560px popup. h-8
      // dropped (would clip the 2nd line); align-bottom keeps the single-
      // line group header and the multi-line numeric headers on one baseline.
      className={`w-[92px] px-3 py-2 align-bottom ${alignClass} text-[12px] font-semibold text-text-3`}
    >
      {/* TD-02 (DEC-075): alignClass on the BUTTON too — the th's
          text-align doesn't reach into a button (UA default centers), so
          a wrapped label in the right/center columns would render
          centered, exactly like the CR-32 Rankings bug. Belt-and-
          suspenders with the global button{text-align:inherit} guard. */}
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`group inline-flex items-center gap-1 transition hover:text-navy ${buttonJustify} ${alignClass}`}
        aria-label={`Sort by ${label.toLowerCase()}, currently ${ariaSort}. Click to toggle.`}
      >
        <span className="whitespace-normal text-balance leading-[1.15]">{label}</span>
        <span
          aria-hidden="true"
          className={`inline-block text-[10px] leading-none transition-transform ${
            isActive
              ? dir === 'desc'
                ? 'rotate-180 opacity-100'
                : 'opacity-100'
              : 'opacity-30'
          }`}
        >
          ▲
        </span>
      </button>
    </th>
  );
}
